const calculateMaleAgePoints = (age: number): number => {
    if (age < 30) {
        return 0;
    }

    if (age < 40) {
        return 3;
    }

    if (age < 50) {
        return 8;
    }

    if (age < 60) {
        return 15;
    }

    if (age < 70) {
        return 24;
    }

    return 32;
};

const calculateFemaleAgePoints = (age: number): number => {
    if (age < 30) {
        return 0;
    }

    if (age < 40) {
        return 1;
    }

    if (age < 50) {
        return 3;
    }

    if (age < 60) {
        return 6;
    }

    if (age < 70) {
        return 11;
    }

    return 18;
};

const addPainScore = (score: number, painLevel: number): number => {
    return score * (
        1 + painLevel / 100
    );
};

const calculateChestPainPoints = (pain: 'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac', painLevel: number): number => {
    switch (pain) {
        case 'Cardiac':
            return addPainScore(15, painLevel);
        case 'Possibly Cardiac':
            return addPainScore(8, painLevel);
        case 'Non-Cardiac':
            return 0;
    }
};

// const MAX_SCORE = {
//     male: 69.5,
//     female: 55.5,
// };

const KAISER_URL = 'https://kp.org';

const getPatientScore = (score: number): number => {
    if (score < 10) {
        return 9;
    }

    if (score < 20) {
        return 35;
    }

    if (score < 30) {
        return 68;
    }

    return 94;
};

const calculateScore = (
    age: number,
    sex: 'male' | 'female',
    chestPain: 'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac',
    painLevel: number,
    diabetes: boolean,
    hypertension: boolean,
    dyslipidemia: boolean,
    smoker: boolean,
    familyHistory: boolean,
) => {

    const ageFunction = sex === 'male' ? calculateMaleAgePoints : calculateFemaleAgePoints;
    let score = ageFunction(age);
    score += calculateChestPainPoints(chestPain, painLevel);

    if (diabetes) {
        score += 6;
    }

    if (hypertension) {
        score += 4;
    }

    if (dyslipidemia) {
        score += 4;
    }

    if (smoker) {
        score += 4;
    }

    if (familyHistory) {
        score += 3;
    }

    return getPatientScore(score);
    // return Math.round((
    //     score / MAX_SCORE[sex]
    // ) * 100);
};

const getScorePosition = (score: number) => {
    const position = 100 - score;
    if (position < 0.5) {
        return 0.5;
    }

    if (position > 96.3) {
        return 96.3;
    }

    return position;
};

const PAIN_LEVEL_MARKS = [
    { value: 0, label: 0 },
    { value: 5, label: 5 },
    { value: 10, label: 10 },
];

const getRecommendationForScore = (
    score: number,
    canExercise?: 'yes' | 'no',
    wasECGWithin6Months?: 'yes' | 'no',
    wasECGInterpretable?: 'yes' | 'no',
    ctaExpertiseAvailable?: 'yes' | 'no',
    age?: number,
): string => {
    if (score < 1) {
        return '';
    }

    if (score <= 15) {
        return 'XRay (optional)';
    }

    if (score >= 85) {
        return 'Calling Cardiologist urgently';
    }

    const ageValue = age || 0;

    if (canExercise === 'no') {
        if (ctaExpertiseAvailable === 'yes') {
            return 'CTA';
        }

        return 'Pharmacologic Stress Test (MPI or stress echo)';
    }

    if (wasECGWithin6Months === 'no') {
        return 'ECG in HealthConnect';
    }

    if (wasECGInterpretable === 'yes') {
        return 'Treadmill Stress Test';
    }

    if (ageValue >= 70) {
        return 'Pharmacologic Stress Test (MPI or stress echo)';
    }

    if (ctaExpertiseAvailable === 'no') {
        return 'Pharmacologic Stress Test (MPI or stress echo)';
    }


    return 'CTA';
};

const getRecommendationSummary = (
    age: number,
    sex: 'male' | 'female',
    chestPain: 'cardiac' | 'possibly-cardiac' | 'non-cardiac',
    diabetes: boolean,
    hypertension: boolean,
    dyslipidemia: boolean,
    smoker: boolean,
    coronaryCalciumScoreAvailable: boolean,
    coronaryCalciumScore: number,
    score: number,
    recommendation: string,
): string => {
    let ageSummary = age > 0 ? `${age} years old ` : '';
    let sexSummary = sex === 'male' ? 'Male' : 'Female';
    let chestPainSummary = '';
    if (chestPain === 'cardiac') {
        chestPainSummary = ' who has typical chest pain.';
    } else if (chestPain === 'possibly-cardiac') {
        chestPainSummary = ' who has atypical chest pain.';
    }
    const risks = [];
    if (diabetes) {
        risks.push('Diabetes');
    }

    if (hypertension) {
        risks.push('Hypertension');
    }

    if (dyslipidemia) {
        risks.push('Dyslipidemia');
    }

    if (smoker) {
        risks.push('Current or Past Smoker');
    }
    const risksSummary = risks.length > 0 ? ' Cardiac risk factors: ' + risks.join(', ') + '.' : '';

    let coronaryCalciumScoreSummary = '';
    if (coronaryCalciumScoreAvailable && coronaryCalciumScore > 0) {
        coronaryCalciumScoreSummary = ` Coronary Calcium: ${coronaryCalciumScore}.`;
    }

    let scoreLvl = 'Low';
    if (score > 15) {
        scoreLvl = 'Intermediate';
    }
    if (score > 85) {
        scoreLvl = 'High';
    }
    const scoreSummary = ` The calculated pre-test probability of CAD is ${score}%. This is consistent with ${scoreLvl} pre-test Probability.`;

    const recommendationSummary = ` The recommended course of action: Consider ${recommendation}. (Based on current TPMG and AHA/ACC guidelines.)`;

    return `${ageSummary}${sexSummary}${chestPainSummary}${risksSummary}${coronaryCalciumScoreSummary}${scoreSummary}${recommendationSummary}`;
};

const getScoreDisplayValue = (score: number): string => {
    if (score < 15) {
        return '<15%';
    }

    if (score < 50) {
        return '15-50%';
    }

    if (score < 85) {
        return '50-85%';
    }

    return '>85%';
};

export {
    KAISER_URL,
    PAIN_LEVEL_MARKS,
    calculateScore,
    getRecommendationForScore,
    getRecommendationSummary,
    getScorePosition,
    getScoreDisplayValue,
};
